/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { cloneDeep, get } from 'lodash';

const ILOC_LEGAL_TEXT_LINE_KEYS = ['1', '2', '3', '4', '5'];

/** ILOC `legalText[0]` may be `{ "1".."5" }`; expand to five strings for `legalText0`..`4`. */
export const normalizeCustomDataCsLegalText = ticketLike => {
  const linesByKey = ticketLike?.customData?.CS?.legalText?.[0];
  if (!linesByKey || typeof linesByKey !== 'object' || Array.isArray(linesByKey)) return;

  let hasLine = false;
  const lines = ILOC_LEGAL_TEXT_LINE_KEYS.map(key => {
    if (key in linesByKey) hasLine = true;
    return String(linesByKey[key] ?? '');
  });

  if (hasLine) ticketLike.customData.CS.legalText = lines;
};

export const computeCodOrderAmt = ticket => {
  const existing = get(ticket, 'customData.CS.orderDetails.cod_order_amt');

  if (existing !== undefined && existing !== null && existing !== '') {
    return existing;
  }

  const sub = Number(get(ticket, 'priceSummary.subtotalPrice'));
  const tax = Number(get(ticket, 'priceSummary.taxAmount'));

  if (!Number.isFinite(sub) && !Number.isFinite(tax)) {
    return undefined;
  }

  return (Number.isFinite(sub) ? sub : 0) + (Number.isFinite(tax) ? tax : 0);
};

export const computeConstituentsFromConsumptions = consumptions => {
  const concrete = [];
  const admix = [];

  for (const c of consumptions || []) {
    const line = [c?.item?.id, c?.item?.description].filter(Boolean).join(' — ');

    if (!line) {
      continue;
    }

    const haystack = `${c?.item?.type || ''} ${c?.item?.description || ''} ${c?.item?.id || ''}`.toLowerCase();

    if (haystack.includes('admix')) {
      admix.push(line);
    } else {
      concrete.push(line);
    }
  }

  return {
    concreteConstituents: concrete.join('; '),
    admixConstituents: admix.join('; '),
  };
};

export const buildTemplateContext = ({ ticket, cocData = [] }) => {
  const ctx = cloneDeep(ticket) || {};
  const cod = computeCodOrderAmt(ticket);

  ctx.customData = ctx.customData || {};
  ctx.customData.CS = ctx.customData.CS || {};
  ctx.customData.CS.orderDetails = { ...ctx.customData.CS.orderDetails };

  const currentCod = ctx.customData.CS.orderDetails.cod_order_amt;

  if ((currentCod === undefined || currentCod === null || currentCod === '') && cod !== undefined) {
    ctx.customData.CS.orderDetails.cod_order_amt = cod;
  }

  const { concreteConstituents, admixConstituents } = computeConstituentsFromConsumptions(cocData);

  ctx.concreteConstituents = concreteConstituents;
  ctx.admixConstituents = admixConstituents;

  normalizeCustomDataCsLegalText(ctx);

  return ctx;
};
